import { useState, useCallback } from 'react';

/**
 * Custom hook for managing phone numbers in the pricing calculator
 * @param {Function} isProductSelected - Function to check if a product is selected
 * @param {Function} toggleProduct - Function to toggle product selection
 * @returns {Object} - Phone numbers management functions and data
 */
const usePhoneNumbers = (initialState = [], isProductSelected, toggleProduct) => {
  // Phone numbers state
  const [phoneNumbers, setPhoneNumbers] = useState(initialState);
  const [showPhoneNumberForm, setShowPhoneNumberForm] = useState(false);
  const [phoneNumberName, setPhoneNumberName] = useState('');
  const [phoneNumberQuantity, setPhoneNumberQuantity] = useState(1);
  const [phoneNumberPrice, setPhoneNumberPrice] = useState(1);
  const [editingPhoneNumber, setEditingPhoneNumber] = useState(null);
  
  // Add a new phone number
  const handleAddPhoneNumber = useCallback(() => {
    if (!phoneNumberName.trim()) return;
    
    if (editingPhoneNumber) {
      // Update existing phone number
      setPhoneNumbers(prev => prev.map(item => 
        item.id === editingPhoneNumber.id 
          ? { 
              ...item, 
              name: phoneNumberName, 
              quantity: phoneNumberQuantity, 
              price: phoneNumberPrice 
            } 
          : item
      ));
    } else {
      // Add new phone number
      setPhoneNumbers(prev => [
        ...prev, 
        { 
          id: Date.now(), 
          name: phoneNumberName, 
          quantity: phoneNumberQuantity, 
          price: phoneNumberPrice 
        }
      ]);
    }
    
    // Make sure phone numbers product is selected
    if (isProductSelected && !isProductSelected('phone-numbers')) {
      toggleProduct('phone-numbers');
    }
    
    // Reset form
    setPhoneNumberName('');
    setPhoneNumberQuantity(1);
    setPhoneNumberPrice(1);
    setShowPhoneNumberForm(false);
    setEditingPhoneNumber(null);
  }, [
    phoneNumberName, 
    phoneNumberQuantity, 
    phoneNumberPrice, 
    editingPhoneNumber, 
    isProductSelected, 
    toggleProduct
  ]);
  
  // Remove a phone number
  const handleDeletePhoneNumber = useCallback((id) => {
    setPhoneNumbers(prev => prev.filter(p => p.id !== id));
    
    // If no phone numbers left, deselect the product
    if (phoneNumbers.length <= 1 && toggleProduct) {
      toggleProduct('phone-numbers');
    }
  }, [phoneNumbers, toggleProduct]);
  
  const reset = useCallback((newState = []) => {
    setPhoneNumbers(newState);
  }, []);

  // Edit a phone number
  const handleEditPhoneNumber = useCallback((updatedItem) => {
    // Якщо передано об'єкт з оновленими даними, оновлюємо номер
    if (updatedItem && updatedItem.id) {
      setPhoneNumbers(prev => prev.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      ));
    }
  }, []);
  
  return {
    // Phone numbers state
    phoneNumbers,
    setPhoneNumbers,
    showPhoneNumberForm,
    setShowPhoneNumberForm,
    phoneNumberName,
    setPhoneNumberName,
    phoneNumberQuantity,
    setPhoneNumberQuantity,
    phoneNumberPrice,
    setPhoneNumberPrice,
    editingPhoneNumber,
    setEditingPhoneNumber,
    
    // Phone numbers functions
    handleAddPhoneNumber,
    handleDeletePhoneNumber,
    handleEditPhoneNumber,
    reset
  };
};

export default usePhoneNumbers;
